import { pageBreakTag } from '../extensions/pageBreak';
import { MarkdownTableCell } from '../extensions/table/types';
import { parseMarkdown } from '../parseMarkdown';
import { IMarkdownConfiguration, RemarkableExtension } from '../types';
import { MarkdownBlock } from './elements/MarkdownBlock';
import { MarkdownDialogBox, TDialogBoxTypes } from './elements/dialogBox';
import { MarkdownHeader } from './elements/header';
import { MarkdownList } from './elements/list';
import { MarkdownPage } from './elements/page';
import { MarkdownParagraph } from './elements/paragraph';
import { MarkdownTable } from './elements/table';
import {
  MarkdownTableOfContents,
  TMDTocProps,
} from './elements/tableOfContents';

export class MarkdownBuilder {
  protected blocks: MarkdownBlock[] = [];
  protected extensions: RemarkableExtension[] = [];

  setExtensions(extensions: RemarkableExtension[]) {
    this.extensions = extensions;
    return this;
  }

  custom(block: MarkdownBlock) {
    this.blocks.push(block);
  }

  h1(text: string) {
    const heading = new MarkdownHeader(text);

    this.blocks.push(heading);

    return heading;
  }

  h2(text: string) {
    const heading = new MarkdownHeader(text, '##');

    this.blocks.push(heading);

    return heading;
  }

  h3(text: string) {
    const heading = new MarkdownHeader(text, '###');

    this.blocks.push(heading);

    return heading;
  }

  h4(text: string) {
    const heading = new MarkdownHeader(text, '####');

    this.blocks.push(heading);

    return heading;
  }

  h5(text: string) {
    const heading = new MarkdownHeader(text, '#####');

    this.blocks.push(heading);

    return heading;
  }

  h6(text: string) {
    const heading = new MarkdownHeader(text, '######');

    this.blocks.push(heading);

    return heading;
  }

  html(text: string) {
    this.blocks.push({
      toString() {
        return text.replaceAll(/\s{2,}/g, ' ');
      },
    });
  }

  p(text: string) {
    const p = new MarkdownParagraph(text);

    this.blocks.push(p);

    return p;
  }

  page() {
    const page = new MarkdownPage();

    this.blocks.push(page);

    return page;
  }

  list(sorted = false) {
    const list = new MarkdownList(sorted);

    this.blocks.push(list);

    return list;
  }

  dialogBox(text: string, level: TDialogBoxTypes = 'success') {
    const box = new MarkdownDialogBox(text, level);

    this.blocks.push(box);

    return box;
  }

  pageBreak() {
    this.blocks.push({
      toString() {
        // @see pageBreak.ts
        return `
${pageBreakTag}`;
      },
    });
  }

  table(header: MarkdownTableCell[] = [], rows: MarkdownTableCell[][] = []) {
    const table = new MarkdownTable(header, rows);

    this.blocks.push(table);

    return table;
  }

  /**
   * Este método debería ser llamado una única vez dentro de un documento,
   * agrega la tabla de contenidos la html
   */
  tableOfContents() {
    this.blocks.push(new MarkdownTableOfContents());
  }

  /**
   * Este método recibe un html y le agrega la tabla de contenidos, dentro de
   * un div con id="TableOfContents"
   */
  addTableOfContents(props: TMDTocProps, html: string): string {
    return MarkdownTableOfContents.appendTableOfContents(props, html);
  }

  toMarkdown(): string {
    let markdown = this.blocks
      .map((current) => current.toString())
      .join('\n\n');

    markdown = markdown.replaceAll(/ *\n *\n *\n/g, '\n\n');

    return markdown;
  }

  toHtml(
    options: Pick<
      IMarkdownConfiguration,
      'additionalStyles' | 'extensions' | 'htmlTemplate' | 'title' | 'tocProps'
    >,
  ): Promise<string> {
    let content = this.toMarkdown();

    if (options?.tocProps)
      content = this.addTableOfContents(options.tocProps, content);

    return parseMarkdown({ ...options, content });
  }
}
